import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Typography, Box, Button, IconButton, Tooltip } from "@material-ui/core";
import pick from "lodash/pick";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useFormik, yupToFormErrors } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, Link } from "react-router-dom";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import DeleteIcon from "@material-ui/icons/Delete";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import {
  Input,
  Select,
  InputOnlyNumbers,
  Autocomplete,
  DatePicker, Checkbox,
} from "../Shared/mui-formik-inputs";
import { OrderSchema, ReturnOrderSchema } from "constants/validation-schemas";
import { OrderFormAllowedFields } from "constants/forms-submit-allowed-fields";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import { formatCustomerOrderLabel } from "util/orders/formatCustomerLabel";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "redux/slices/userSlice";
import { sortOrder } from "util/customers/deliveryTimings";
import { Alert } from "@material-ui/lab";
import { usePermission } from "hooks/usePermission";
import { getSupplierLocations, selectSupplierLocations } from "../../redux/slices/supplierLocationsSlice";
import {
  fetchNextOrderNumber,
  selectNextOrderNumber,
  selectOrderNumbersError,
} from "../../redux/slices/orderNumbersSlice";

// Import additional components
import {
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography as MuiTypography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import AddIcon from "@material-ui/icons/Add";
import AddPackagesDialog from "components/PackagesModal/AddPackagesModal";
import { useLocation } from "react-router-dom";
import { deleteOrder, fetchAvailableDepartures } from "redux/slices/orderSlice";

const locales = {
  "en-us": "en",
  "en-gb": "en-gb",
  en: "en",
  de: "de",
  "de-de": "de",
};
const orderTypeList = [
  { title: "Customer Delivery", value: "delivery" },
  { title: "Return", value: "return" },
];
const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    fontSize: "35px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
  },
  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "44px",
  },
  sectionTitle: {
    font: "normal normal 500 22px/32px Roboto",
    color: "#121212",
    marginTop: "24px",
    marginBottom: "16px",
  },
  verifiedSection: {
    backgroundColor: "#f3f8ff",
    border: "1px solid #dae6fc",
    borderRadius: "4px",
    marginBottom: "24px",
  },
  unverifiedSection: {
    backgroundColor: "#fff5f5",
    border: "1px solid #ffdddd",
    borderRadius: "4px",
    marginBottom: "24px",
  },
  packageList: {
    maxHeight: "250px",
    overflow: "auto",
  },
  verifiedChip: {
    backgroundColor: "#e0f0e0",
    color: "#2e7d32",
    marginLeft: "8px",
  },
  unverifiedChip: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    marginLeft: "8px",
  },
  packageItem: {
    padding: "4px 16px",
  },
  noPackagesMessage: {
    padding: "16px",
    color: "#666",
    fontStyle: "italic",
  },
  deliveryNoteSummary: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  },
  packageCount: {
    fontSize: "14px",
    color: "#666",
    marginLeft: "8px",
  },
  tooltipCustom: {
    backgroundColor: "#6F9CEB !important",
    color: "#fff",
    fontSize: "0.875rem",
  },
  button: {
    color: 'gray',

    '& .cancel-save-btn-text': {
      opacity: '0',
      fontSize: '0.7rem !important',
    },

    '& svg': {
      transition: 'font-size 0.5s',
    },

    '&:hover, &:focus': {
      '& svg': {
        fontSize: '1.8rem',
        transition: 'font-size 0.5s',
      },

      '& .cancel-save-btn-text': {
        opacity: '1',
        transition: 'opacity 0.5s',
      },
    },
  },
});

const OrderForm = ({ initialValues, onSubmit, action, customerList, routes, readOnly = false }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [departureOptions, setDepartureOptions] = useState([]);
  const [departureLoading, setDepartureLoading] = useState(false);
  const [supplierIdentifier, setSupplierIdentifier] = useState(null);
  const [isSlotsAvailable, setIsSlotsAvailable] = useState(true);
  const [selectCustomer, setSelectCustomer] = useState(null);
  const [customerOutsideAllowedRadius, setCustomerOutsideAllowedRadius] = useState(false);
  const [customerGeoLocationStatusError, setCustomerGeoLocationStatusError] = useState(false);
  const [prevCustomerId, setPrevCustomerId] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState([
    {
      delivery_note_number: "",
      packages: 1,
      auto: false,
    },
  ]);
  const [newDeliveryNotesCount, setNewDeliveryNotesCount] = useState(0);
  const [selectedPackages, setSelectedPackages] = useState({});
  const [packagesToDelete, setPackagesToDelete] = useState([]); // Add this state to track packages to delete
  const [addPackagesOpen, setAddPackagesOpen] = useState(false);
  const [packageCount, setPackageCount] = useState(1);
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [newPackages, setNewPackages] = useState({});
  const user = useSelector(selectUser);
  const supplierLocations = useSelector(selectSupplierLocations);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fromReorderModal = queryParams.get("from") === "reorderStopsModal";
  const allowAtbasPackageRemoval = readOnly && fromReorderModal;

  const nextOrderNumber = useSelector(selectNextOrderNumber);
  const orderNumbersError = useSelector(selectOrderNumbersError);

  useEffect(() => {
    if (initialValues?.DeliveryNotes && Array.isArray(initialValues.DeliveryNotes)) {
      setDeliveryNotes(
        initialValues.DeliveryNotes.map((note) => ({
          delivery_note_number: note.delivery_note_number || "",
          packages: note.packages || 0,
          auto: note.auto ?? false,
        }))
      );
      setNewDeliveryNotesCount(0);
    } else if (initialValues?.delivery_notes && Array.isArray(initialValues.delivery_notes)) {
      setDeliveryNotes(
        initialValues.delivery_notes.map((note) => ({
          delivery_note_number: note.delivery_note_number || "",
          packages: note.packages || 0,
          auto: note.auto || false,
        }))
      );
      setNewDeliveryNotesCount(0);
    } else if (initialValues?.number) {
      setDeliveryNotes([
        {
          delivery_note_number: initialValues.number || "",
          packages: initialValues.packages || 0,
          auto: false,
        },
      ]);
      setNewDeliveryNotesCount(0);
    }
  }, [initialValues]);

  useEffect(() => {
    if (selectCustomer) {
      setSupplierIdentifier(selectCustomer?.Supplier?.identifier);
    }
  }, [selectCustomer]);

  useEffect(() => {
    if (!supplierIdentifier) return;
    if (nextOrderNumber && deliveryNotes.length > 0 && action === "ADD") {
      const updatedNotes = deliveryNotes.map((note, index) => {
        if (note.auto) {
          const baseNumber = parseInt(nextOrderNumber) || 0;
          const incrementedNumber = (baseNumber + index).toString().padStart(6, "0");
          return {
            ...note,
            delivery_note_number: `${supplierIdentifier}-${incrementedNumber}`,
          };
        }
        return note;
      });
      setDeliveryNotes(updatedNotes);
    }
  }, [nextOrderNumber, supplierIdentifier, action]);

  useEffect(() => {
    if (action === "ADD" || action === "EDIT") {
      dispatch(fetchNextOrderNumber());
    }
  }, [action, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validate: (values) => {
      const schema = values?.order_type === "return" ? ReturnOrderSchema : OrderSchema;
      try {
        schema.validateSync(values, { abortEarly: false });
        return {};
      } catch (err) {
        return yupToFormErrors(err);
      }
    },
    initialValues: {
      customer_id: initialValues?.customer_id || "",
      description: initialValues?.description || "",
      departure: initialValues?.departure || null,
      departure_date: initialValues?.departure_date
        ? moment(`${initialValues?.departure_date} 12:00:00`, "YYYY-MM-DD HH:mm:ss").utc().toDate()
        : moment().utc().startOf("day").toDate(),
      remarks: initialValues?.remarks || "",
      express: initialValues?.express ?? false,
      supplier_id: initialValues?.supplier_id ?? null,
      supplier_location_id: initialValues?.supplier_location_id || user?.supplier_location_id || null,
      order_type: initialValues?.order_type ?? orderTypeList[0].value,
      delivery_notes: initialValues?.DeliveryNotes
        ? initialValues.DeliveryNotes.map((note) => ({
          delivery_note_number: note.delivery_note_number || "",
          packages: note.packages ?? 1,
          auto: note.auto ?? false,
        }))
        : [
          {
            delivery_note_number: "",
            packages: 1,
            auto: false,
          },
        ],
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (readOnly && (!allowAtbasPackageRemoval || packagesToDelete.length === 0)) {
          setSubmitting(false);
          return;
        }
        const preparedDeliveryNotes = deliveryNotes.map((note, index) => {
          if (action === "EDIT") {
            const existingNote = initialValues?.DeliveryNotes?.find(
              (apiNote) => apiNote.delivery_note_number === note.delivery_note_number
            );

            if (existingNote) {
              const baseNote = {
                id: existingNote.id,
                delivery_note_number: note.delivery_note_number,
                packages: note.packages,
                auto: note.auto,
              };

              if (fromReorderModal) {
                const packagesForNote = existingNote.Packages
                  ? existingNote.Packages.filter((pkg) => !packagesToDelete.includes(pkg.id)).map((pkg) => {
                    const { unique_package_id, qr_code, verified_at, delivered_at, ...allowedPackageFields } = pkg;
                    return allowedPackageFields;
                  })
                  : [];

                return {
                  ...baseNote,
                  Packages: packagesForNote,
                };
              } else {
                return baseNote;
              }
            } else {
              const baseNote = {
                delivery_note_number: note.delivery_note_number,
                packages: note.packages,
                auto: note.auto,
              };

              if (fromReorderModal) {
                return {
                  ...baseNote,
                  Packages: [],
                };
              } else {
                return baseNote;
              }
            }
          } else {
            if (note.auto) {
              const baseNumber = parseInt(nextOrderNumber) || 0;
              const incrementedNumber = (baseNumber + index).toString().padStart(6, "0");
              return {
                delivery_note_number: `${supplierIdentifier}-${incrementedNumber}`,
                packages: note.packages,
                auto: note.auto,
              };
            } else {
              return {
                delivery_note_number: note.delivery_note_number,
                packages: note.packages,
                auto: note.auto,
              };
            }
          }
        });

        const finalPayload = {
          ...pick(values, OrderFormAllowedFields),
          departure_date: values.departure_date?.format
            ? values.departure_date.format("YYYY-MM-DD")
            : values.departure_date,
          remarks: values.remarks || null,
          delivery_notes: preparedDeliveryNotes,
        };

        await onSubmit(finalPayload);
      } catch (err) {
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, handleChange, errors, handleSubmit, setFieldValue, submitCount, isValid, isSubmitting } = formik;
  let { handleBlur } = formik;
  if (!submitCount) {
    handleBlur = null;
  }
  useEffect(() => {
    if (deliveryNotes.length > 0) {
      const validatedDeliveryNotes = deliveryNotes.map((note) => ({
        delivery_note_number: note.delivery_note_number || "",
        packages: note.packages || 0,
        auto: note.auto || false,
      }));
      setFieldValue("delivery_notes", validatedDeliveryNotes, false);
    }
  }, [deliveryNotes, setFieldValue]);


  useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      console.log("Form validation errors:", errors);
    }
  }, [errors, submitCount]);

  const hasAnyAutoGenerateChecked = deliveryNotes.some(note => note.auto === true);
  useEffect(() => {
    if (deliveryNotes.length > 0) {
      const validatedDeliveryNotes = deliveryNotes.map((note) => ({
        delivery_note_number: note.delivery_note_number || "",
        packages: note.packages || 0,
        auto: note.auto || false,
      }));
      setFieldValue("delivery_notes", validatedDeliveryNotes, false);
    }
  }, [deliveryNotes, setFieldValue]);


  useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      console.log("Form validation errors:", errors);
    }
  }, [errors, submitCount]);

  const closeOrderHandler = () => {
    const routeId = queryParams.get("routeId");
    const fromPage = queryParams.get("fromPage") || "/routes/current";
    if (fromReorderModal && routeId) {
      history.push(`${fromPage}?openReorderModal=true&routeId=${routeId}`);
    } else {
      history.push(PATHS.orders.root);
    }
  };
  const customerListWithLabels = useMemo(() => {
    const customersToShow = customerList;

    return customersToShow?.map((customer) => ({
      ...customer,
      label: formatCustomerOrderLabel(customer),
    }));
  }, [customerList, user?.permissions?.orders?.show_supplier]);

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  const lastElementRef = useRef(null);
  useEffect(() => {
    const handleTabPress = (e) => {
      if (e.key === "Tab") {
        if (lastElementRef.current && lastElementRef.current.contains(document.activeElement)) {
          e.preventDefault();
          if (cancelButtonRef.current) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            cancelButtonRef.current.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleTabPress);
    return () => {
      document.removeEventListener("keydown", handleTabPress);
    };
  }, []);
  useEffect(() => {
    dispatch(getSupplierLocations());
  }, [dispatch]);

  useEffect(() => {
    if (values.customer_id) {
      const selectedCustomer = customerListWithLabels.find((c) => c.id === values.customer_id);
      const customersToShow = customerList;
      const customer = customersToShow.find((c) => c.id === values?.customer_id);

      if (values.departure && customer) {
        const timings = customer.CustomerDeliveryTimings || [];
        const matchingTiming = timings.find(
          (cdt) => cdt.ToursDeliveryTiming?.delivery_timing?.toLowerCase() === values.departure?.toLowerCase()
        );
        const activeTour = matchingTiming?.ToursDeliveryTiming?.Tour || customer.Tour;
        const allowedSupplierLocations = activeTour?.ToursAlloweds?.map((tourAllowed) => tourAllowed?.supplier_location_id);
        const supplierLocationToCheck = values?.supplier_location_id || user?.supplier_location_id;
        setCustomerOutsideAllowedRadius(!allowedSupplierLocations?.includes(supplierLocationToCheck));
      } else {
        setCustomerOutsideAllowedRadius(false);
      }

      setSelectCustomer(selectedCustomer);
      setFieldValue("express", selectedCustomer?.priority || values?.express || false);
      if (selectedCustomer?.geo_location_status === 'error') {
        setCustomerGeoLocationStatusError(true);
      }
      if (action === "ADD") {
        if (prevCustomerId !== values.customer_id) {
          setFieldValue("remarks", selectedCustomer?.default_remarks || "");
        }
      } else if (action === "EDIT") {
        if (prevCustomerId && prevCustomerId !== values.customer_id) {
          setFieldValue("remarks", selectedCustomer?.default_remarks || "");
        }
      }
      setPrevCustomerId(values.customer_id);
    } else {
      setCustomerOutsideAllowedRadius(false);
    }
  }, [values.customer_id, values.departure_date, values.departure, customerListWithLabels, values?.supplier_location_id, t]);

  useEffect(() => {
    if (!values.customer_id || !values.departure_date) {
      setDepartureOptions([]);
      setIsSlotsAvailable(true);
      return;
    }

    let cancelled = false;

    const loadDepartures = async () => {
      setDepartureLoading(true);
      try {
        const params = {
          customer_id: values.customer_id,
          departure_date: moment(values.departure_date).format("YYYY-MM-DD"),
          supplier_location_id: values?.supplier_location_id || user?.supplier_location_id || "",
        };
        if (action === "EDIT" && initialValues?.id) {
          params.exclude_order_id = initialValues.id;
        }

        const availability = await dispatch(fetchAvailableDepartures(params));
        if (cancelled) return;

        const availableTimings = (availability || [])
          .filter((slot) => slot.available)
          .map((slot) => slot.timing)
          .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b));

        setIsSlotsAvailable(availableTimings.length > 0);
        setDepartureOptions(
          availableTimings.map((timing) => ({ label: t(timing), value: timing }))
        );

        if (action === "ADD" && values.departure && !availableTimings.includes(values.departure)) {
          setFieldValue("departure", null);
        }
      } catch (err) {
        if (!cancelled) {
          setDepartureOptions([]);
          setIsSlotsAvailable(false);
        }
      } finally {
        if (!cancelled) {
          setDepartureLoading(false);
        }
      }
    };

    loadDepartures();

    return () => {
      cancelled = true;
    };
  }, [values.customer_id, values.departure_date, values?.supplier_location_id, action, initialValues?.id, dispatch, t]);

  const addDeliveryNote = () => {
    if (action === "EDIT") {
      setDeliveryNotes([
        ...deliveryNotes,
        {
          delivery_note_number: "",
          packages: 1,
          auto: false,
        },
      ]);

      setNewDeliveryNotesCount(newDeliveryNotesCount + 1);
    } else {
      setDeliveryNotes([
        ...deliveryNotes,
        {
          delivery_note_number: "",
          packages: 1,
          auto: false,
        },
      ]);
    }
  };

  const removeDeliveryNote = (index) => {
    if (deliveryNotes.length > 1) {
      setDeliveryNotes(deliveryNotes.filter((_, i) => i !== index));
    }
  };

  const updateDeliveryNote = (index, field, value) => {
    const updatedNotes = [...deliveryNotes];
    if (field === "packages") {
      // Enforce maximum of 99 packages
      const numValue = parseInt(value) || 0;
      updatedNotes[index] = {
        ...updatedNotes[index],
        [field]: Math.min(99, Math.max(0, numValue))
      };
    } else {
      updatedNotes[index] = { ...updatedNotes[index], [field]: value };
    }
    setDeliveryNotes(updatedNotes);
  };

  const handleAutoToggle = async (index, auto) => {
    const updatedNotes = [...deliveryNotes];

    const originalDeliveryNoteNumber = updatedNotes[index].delivery_note_number;
    const wasAutoGenerated = originalDeliveryNoteNumber && originalDeliveryNoteNumber.startsWith(`${supplierIdentifier}-`);

    updatedNotes[index].auto = auto;

    if (auto) {
      if (wasAutoGenerated) {
      } else if (nextOrderNumber && supplierIdentifier) {
        const baseNumber = parseInt(nextOrderNumber) || 0;
        const incrementedNumber = (baseNumber + index).toString().padStart(6, "0");
        updatedNotes[index].delivery_note_number = `${supplierIdentifier}-${incrementedNumber}`;
      }
    } else if (!auto) {
      const existingNote = initialValues?.DeliveryNotes?.find((apiNote, i) => i === index);

      if (existingNote && existingNote.delivery_note_number) {
        updatedNotes[index].delivery_note_number = existingNote.delivery_note_number;
      } else if (originalDeliveryNoteNumber && !wasAutoGenerated) {
        updatedNotes[index].delivery_note_number = originalDeliveryNoteNumber;
      } else {
        updatedNotes[index].delivery_note_number = "";
      }
    }

    setDeliveryNotes(updatedNotes);
  };

  const handleDeletePackage = (packageId, deliveryNoteId) => {
    setPackagesToDelete([...packagesToDelete, packageId]);

    const updatedNotes = [...deliveryNotes];
    const noteIndex = updatedNotes.findIndex((note) => {
      const existingNote = initialValues?.DeliveryNotes?.find((apiNote) => apiNote.id === deliveryNoteId);
      return existingNote && note.delivery_note_number === existingNote.delivery_note_number;
    });

    if (noteIndex !== -1) {
      updatedNotes[noteIndex] = {
        ...updatedNotes[noteIndex],
        packages: Math.max(0, (updatedNotes[noteIndex].packages || 0) - 1),
      };
      setDeliveryNotes(updatedNotes);
    }
  };

  const openAddPackagesModal = (noteId, noteIndex) => {
    setSelectedDeliveryNote({ id: noteId, index: noteIndex });
    setPackageCount(1);
    setAddPackagesOpen(true);
  };

  const handleAddPackages = () => {
    if (!selectedDeliveryNote) return;

    const count = packageCount === "" ? 0 : parseInt(packageCount);
    if (isNaN(count) || count <= 0) return;
    const updatedNotes = [...deliveryNotes];
    const noteIndex = updatedNotes.findIndex((note) => {
      const existingNote = initialValues?.DeliveryNotes?.find((apiNote) => apiNote.id === selectedDeliveryNote.id);
      return existingNote && note.delivery_note_number === existingNote.delivery_note_number;
    });

    if (noteIndex !== -1) {
      updatedNotes[noteIndex] = {
        ...updatedNotes[noteIndex],
        packages: (updatedNotes[noteIndex].packages || 0) + count,
      };
      setDeliveryNotes(updatedNotes);
    }

    const uniquePrefix = `${selectedDeliveryNote.id}-${Date.now()}`;
    const newPackagesList = {};
    for (let i = 1; i <= count; i++) {
      const uniqueId = `${uniquePrefix}-${i}`;
      newPackagesList[uniqueId] = {
        id: uniqueId,
        package_id: `${t('New package')} ${i}`,
        delivery_note_id: selectedDeliveryNote.id,
        verified_at: null,
        isNew: true,
      };
    }
    setNewPackages({
      ...newPackages,
      ...newPackagesList,
    });

    setAddPackagesOpen(false);
    setPackageCount("");
  };

  const handleDeleteOrder = () => {
    dispatch(deleteOrder(initialValues.id, true))
      .then(() => {
        const routeId = queryParams.get('routeId');
        const fromPage = queryParams.get("fromPage") || "/routes/current";
        if (routeId) {
          history.push(`${fromPage}?openReorderModal=true&routeId=${routeId}`);
        } else {
          history.push(PATHS.orders.root);
        }
      });
  };

  return (
    <MuiPickersUtilsProvider
      utils={MomentUtils}
      locale={locales[i18n.language.toLowerCase()]}
      libInstance={moment}
    >
      <Box height="100%">
        <Box display="flex" mb={4}>
          <Box flex={2}>
            <Typography className={classes._heading} variant="h4">
              {action === "ADD" ? t("New Order") : t("Edit Order")}
            </Typography>
            {readOnly && (
              <Typography style={{ marginTop: 8, color: "#D32F2F" }}>
                {t("editing_disabled_atbas_orders")}
              </Typography>
            )}
          </Box>
          <Box flex={2} textAlign="right" position="relative">
            <Box position="absolute" right={0} top={0} display="flex" alignItems="center">
              <FormCancelSaveButton
                loading={isSubmitting}
                disabled={
                  !isValid ||
                  isSubmitting ||
                  customerOutsideAllowedRadius ||
                  customerGeoLocationStatusError ||
                  (readOnly ? (!allowAtbasPackageRemoval || packagesToDelete.length === 0) : false)
                }
                onCancel={closeOrderHandler}
                onSave={readOnly ? (allowAtbasPackageRemoval ? handleSubmit : undefined) : handleSubmit}
                ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
              />
              {fromReorderModal && action === "EDIT" && (

                <Button
                  onClick={handleDeleteOrder}
                  className={classes.button}
                  type="button"
                  size="large"
                >
                  <span className="cancel-save-btn-text">{t('Delete Order')}</span>
                  &nbsp;
                  <DeleteIcon />

                </Button>
              )}
            </Box>
          </Box>
        </Box>
        {user?.permissions?.orders?.show_supplier && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={6} lg={5}>
              <Autocomplete
                onBlur={handleBlur}
                name="supplier_location_id"
                label="Supplier Location"
                errors={errors}
                value={values.supplier_location_id}
                settings={{
                  disableClearable: true,
                  valueProp: "id",
                  labelProp: "name",
                }}
                onChange={(selected) => {
                  setFieldValue("supplier_location_id", selected?.id || null);
                }}
                options={(supplierLocations || []).filter((location) => location.supplier_id === (values?.supplier_id || user?.supplier_id))}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={fromReorderModal || readOnly}
              />
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={6} lg={2}>
          <Tooltip
            title={action === "EDIT" ? t("Order Type cannot be changed after order creation") : ""}
            placement="top"
            classes={{ tooltip: classes.tooltipCustom }}
          >
            <span style={{ display: "block", width: "100%", cursor: action === "EDIT" ? "not-allowed" : "default" }}>
              <Select
                name="order_type"
                label={t("Order Type") + " *"}
                errors={errors}
                value={values.order_type}
                disabled={action === "EDIT"}
                onBlur={handleBlur}
                onChange={(e) => {
                  const newType = e.target.value;
                  setFieldValue("order_type", newType);

                  if (newType === "return") {
                    setDeliveryNotes((prev) =>
                      prev.map((note) =>
                        note.auto
                          ? {
                            ...note,
                            auto: false,
                            delivery_note_number: "",
                          }
                          : note
                      )
                    );
                  }
                }}
                options={orderTypeList.map((o) => ({ label: t(o.title), value: o.value }))}
                required
              />
            </span>
          </Tooltip>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={5}>
            <Autocomplete
              onBlur={handleBlur}
              name="customer_id"
              label={t("Customer")}
              errors={errors}
              value={values.customer_id}
              settings={{
                disableClearable: true,
                valueProp: "id",
                labelProp: "label",
              }}
              onChange={(selected) => {
                setFieldValue("customer_id", selected.id);
              }}
              options={customerListWithLabels.filter((c) => c.active)}
              required
              disabled={user?.permissions?.orders?.disable_customers_load || fromReorderModal || readOnly}
            />
          </Grid>
        </Grid>
        {nextOrderNumber && hasAnyAutoGenerateChecked && (
          <Box my={2}>
            <Alert severity="info">
              {t("Note")}: {t("Order number may change while order creation")}
            </Alert>
          </Box>
        )}
        {customerOutsideAllowedRadius && (
          <Box my={2}>
            <Alert severity="error">
              {t("Warning")}
              :&nbsp;
              {t(
                "This order will not be created as the customer's location falls outside the permitted delivery radius."
              )}
            </Alert>
          </Box>
        )}
        {customerGeoLocationStatusError && (
          <Box my={2}>
            <Alert
              severity="error"
              action={
                user?.permissions?.customers?.create ? (
                  <Link
                    to={`${PATHS.customers.edit.replace(":id", values.customer_id)}`}
                    style={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      fontWeight: "500",
                      cursor: "pointer",
                      marginLeft: "8px"
                    }}
                  >
                    {t("Edit Customer")}
                  </Link>
                ) : null
              }
            >
              {t("Warning")}:&nbsp;
              {user?.permissions?.customers?.create
                ? t("Customer address contains issues. Please check and update the address.")
                : t("Customer address is invalid. Please contact your administrator.")}
            </Alert>
          </Box>
        )}

        {orderNumbersError && (
          <Box my={2}>
            <Alert severity="error">
              {t("Error")}: {orderNumbersError}
            </Alert>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input
              label={values.order_type === 'return' ? t("Pickup ID") : t("Delivery Note Number")}
              name="delivery_notes[0].delivery_note_number"
              onChange={(e) => updateDeliveryNote(0, "delivery_note_number", e.target.value)}
              onBlur={handleBlur}
              value={deliveryNotes[0]?.delivery_note_number || ""}
              errors={errors}
              disabled={deliveryNotes[0]?.auto || readOnly}
              placeholder={
                deliveryNotes[0]?.auto && supplierIdentifier
                  ? `${supplierIdentifier}-${nextOrderNumber}`
                  : (values.order_type === 'return' ? t("Enter Return ID") : t("Enter delivery note number"))
              }
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputOnlyNumbers
              label={t("Packages")}
              name="delivery_notes[0].packages"
              onChange={(e) => updateDeliveryNote(0, "packages", parseInt(e.target.value) || 0)}
              onBlur={handleBlur}
              value={deliveryNotes[0]?.packages || 0}
              errors={errors}
              min={0}
              max={99}
              disabled={fromReorderModal || readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Box display="flex" alignItems="center" mt={2}>
              <Checkbox
                checked={deliveryNotes[0]?.auto || false}
                value={deliveryNotes[0]?.auto || false}
                name="delivery_notes[0].auto"
                onChange={(e) => handleAutoToggle(0, e.target.checked)}
                onBlur={handleBlur}
                errors={errors}
                style={{ color: "#6F9CEB" }}
                disabled={fromReorderModal}
              />
              <Typography component="span" className="font-size-12">
                {values.order_type === 'return' ? t("Auto-generate return ID") : t("Auto-generate delivery note number")}
              </Typography>
            </Box>
          </Grid>
          {deliveryNotes.length > 1 && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box display="flex" alignItems="center" mt={2}>
                <IconButton
                  aria-label={t("Remove delivery note")}
                  onClick={() => removeDeliveryNote(0)}
                  size="small"
                  disabled={readOnly}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          )}
        </Grid>

        {deliveryNotes.slice(1).map((note, index) => {
          // Check if this is a newly added note (not in initialValues)
          const isNewNote = !initialValues?.DeliveryNotes?.some(existingNote =>
            existingNote.delivery_note_number === note.delivery_note_number);

          return (
            <Grid container spacing={2} key={index + 1} mt={1}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Input
                  label={`${values.order_type === 'return' ? t("Pickup ID") : t("Delivery Note Number")} #${index + 2}`}
                  name={`delivery_notes[${index + 1}].delivery_note_number`}
                  onChange={(e) => updateDeliveryNote(index + 1, "delivery_note_number", e.target.value)}
                  onBlur={handleBlur}
                  value={note.delivery_note_number}
                  errors={errors}
                  disabled={note.auto}
                  placeholder={
                    note.auto && supplierIdentifier
                      ? `${supplierIdentifier}-${nextOrderNumber}`
                      : (values.order_type === 'return' ? t("Enter Return ID") : t("Enter delivery note number"))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <InputOnlyNumbers
                  label={`${t("Packages")} #${index + 2}`}
                  name={`delivery_notes[${index + 1}].packages`}
                  onChange={(e) => updateDeliveryNote(index + 1, "packages", parseInt(e.target.value) || 0)}
                  onBlur={handleBlur}
                  value={note.packages}
                  errors={errors}
                  min={0}
                  max={99}
                  disabled={isNewNote ? readOnly : (fromReorderModal || readOnly)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box display="flex" alignItems="center" mt={2}>
                  <Checkbox
                    checked={note.auto}
                    value={note.auto}
                    name={`delivery_notes[${index + 1}].auto`}
                    onChange={(e) => handleAutoToggle(index + 1, e.target.checked)}
                    onBlur={handleBlur}
                    errors={errors}
                    style={{ color: "#6F9CEB" }}
                    disabled={isNewNote ? false : fromReorderModal}
                  />
                  <Typography component="span" className="font-size-12">
                    {values.order_type === 'return' ? t("Auto-generate return ID") : t("Auto-generate delivery note number")}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box display="flex" alignItems="center" mt={2}>
                  <IconButton
                    aria-label={t("Remove delivery note")}
                    onClick={() => removeDeliveryNote(index + 1)}
                    size="small"
                    disabled={readOnly}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          );
        })}


        <Box mb={3} mt={2}>
          <Button
            variant="outlined"
            onClick={addDeliveryNote}
            style={{
              backgroundColor: "transparent",
              borderColor: "#6F9CEB",
              color: "#6F9CEB",
            }}
            disabled={readOnly}
          >
            {values.order_type === 'return' ? t("Add Return ID") : t("Add Delivery Note")}
          </Button>
        </Box>


        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3} sx={{ pr: 0 }}>
            <DatePicker
              onChange={(date) => {
                if (date && date.isValid()) {
                  setFieldValue("departure_date", date);
                }

                if (date === null) {
                  setFieldValue("departure_date", null);
                }
              }}
              name="departure_date"
              value={values.departure_date}
              errors={errors}
              label="Departure date"
              required
              disablePast
              InputProps={{
                sx: {
                  ".MuiFilledInput-adornedEnd": {
                    paddingRight: "0px",
                  },
                },
              }}
              disabled={fromReorderModal || readOnly}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={2}>
            <Select
              label={isSlotsAvailable ? t("Departure") + " *" : t("No Departures Available")}
              name="departure"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.departure}
              errors={errors}
              options={departureOptions}
              disabled={departureOptions.length === 0 || fromReorderModal || departureLoading}
              InputLabelProps={{
                className: isSlotsAvailable ? "" : "red-label",
              }}
            />
          </Grid>
        </Grid>
        {!usePermission("orders.hide_priority_order") && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={4} lg={3}>
              <Select
                label={t("Express Order") + " *"}
                name="express"
                onChange={(event) => setFieldValue("express", event.target.value === "true")}
                onBlur={handleBlur}
                value={values.express || selectCustomer?.priority ? "true" : "false"}
                errors={errors}
                options={[
                  { label: t("Yes"), value: "true" },
                  { label: t("No"), value: "false" },
                ]}
                disabled={selectCustomer?.priority || fromReorderModal || readOnly}
              />
            </Grid>
          </Grid>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={4} lg={3}>
            <div ref={lastElementRef}>
              <Input
                label={t("Remarks")}
                name="remarks"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.remarks}
                errors={errors}
                disabled={fromReorderModal || readOnly}
              />
            </div>
          </Grid>
        </Grid>

        {action === "EDIT" && initialValues?.DeliveryNotes && fromReorderModal && (
          <>
            <Typography className={classes._subheading} variant="h5">
              {t("Delivery Notes and Packages Status")}
            </Typography>

            <Typography className={classes.sectionTitle}>{t("Scanned/Verified Delivery Notes")}</Typography>

            {initialValues.DeliveryNotes.some(
              (note) =>
                note.Packages && note.Packages.some((pkg) => !!pkg.verified_at && !packagesToDelete.includes(pkg.id))
            ) ? (
              <Paper className={classes.verifiedSection} elevation={0}>
                {initialValues.DeliveryNotes.map((note, noteIndex) => {
                  const verifiedPackages = note.Packages
                    ? note.Packages.filter((pkg) => !!pkg.verified_at && !packagesToDelete.includes(pkg.id))
                    : [];

                  if (verifiedPackages.length === 0) return null;

                  return (
                    <Accordion key={`verified-${note.id || noteIndex}`} defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={classes.deliveryNoteSummary}>
                          <Typography variant="subtitle1">
                            {note.delivery_note_number}
                            <Chip
                              size="small"
                              label={`${verifiedPackages.length} ${t("verified")}`}
                              className={classes.verifiedChip}
                              icon={<CheckCircleIcon style={{ fontSize: 16 }} />}
                            />
                          </Typography>
                          <Typography className={classes.packageCount}>
                            {verifiedPackages.length} /{" "}
                            {note.Packages
                              ? note.Packages.filter((pkg) => !packagesToDelete.includes(pkg.id)).length
                              : 0}{" "}
                            {t("packages verified")}
                          </Typography>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense className={classes.packageList}>
                          {verifiedPackages.map((pkg, pkgIndex) => (
                            <ListItem key={`verified-pkg-${pkg.id || pkgIndex}`} className={classes.packageItem}>
                              <ListItemText primary={pkg.package_id} />
                              <CheckCircleIcon style={{ color: "#4caf50", fontSize: 16, marginLeft: 8 }} />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Paper>
            ) : (
              <Paper className={classes.verifiedSection} elevation={0}>
                <Typography className={classes.noPackagesMessage}>{t("No verified packages found")}</Typography>
              </Paper>
            )}

            <Typography className={classes.sectionTitle}>{t("Unscanned/Unverified Delivery Notes")}</Typography>

            {initialValues.DeliveryNotes.some(
              (note) =>
                (note.Packages && note.Packages.some((pkg) => !pkg.verified_at && !packagesToDelete.includes(pkg.id))) ||
                fromReorderModal
            ) ? (
              <Paper className={classes.unverifiedSection} elevation={0}>
                {initialValues.DeliveryNotes.map((note, noteIndex) => {
                  const unverifiedPackages = note.Packages
                    ? note.Packages.filter((pkg) => !pkg.verified_at && !packagesToDelete.includes(pkg.id))
                    : [];

                  if (unverifiedPackages.length === 0 && !fromReorderModal) return null;

                  return (
                    <Accordion key={`unverified-${note.id || noteIndex}`} defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div className={classes.deliveryNoteSummary}>
                          <Typography variant="subtitle1">
                            {note.delivery_note_number}
                            <Chip
                              size="small"
                              label={`${unverifiedPackages.length} ${t("unverified")}`}
                              className={classes.unverifiedChip}
                              icon={<CancelIcon style={{ fontSize: 16 }} />}
                            />
                          </Typography>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Typography className={classes.packageCount}>
                              {unverifiedPackages.length} /{" "}
                              {note.Packages
                                ? note.Packages.filter((pkg) => !packagesToDelete.includes(pkg.id)).length
                                : 0}{" "}
                              {t("packages unverified")}
                            </Typography>
                          </div>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2} className={classes.packageList}>
                          {unverifiedPackages.map((pkg, pkgIndex) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={`unverified-pkg-${pkg.id || pkgIndex}`}>
                              <Paper
                                elevation={0}
                                className={classes.packageItem}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "8px 12px",
                                  backgroundColor: "#fff5f5",
                                  border: "1px solid #ffdddd",
                                  borderRadius: "4px",
                                }}
                              >
                                <Typography variant="body2" style={{ flex: 1 }}>
                                  {pkg.package_id}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeletePackage(pkg.id, note.id)}
                                  aria-label={t("Delete package")}
                                >
                                  <DeleteOutlineIcon fontSize="small" style={{ color: "#f44336" }} />
                                </IconButton>
                              </Paper>
                            </Grid>
                          ))}

                          {Object.values(newPackages)
                            .filter((pkg) => pkg.delivery_note_id === note.id)
                            .map((pkg) => (
                              <Grid item xs={12} sm={6} md={4} lg={3} key={`new-pkg-${pkg.id}`}>
                                <Paper
                                  elevation={0}
                                  className={classes.packageItem}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                    backgroundColor: "#e0f7fa",
                                    border: "1px solid #b2ebf2",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <Typography variant="body2" style={{ flex: 1 }}>
                                    {pkg.package_id}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const updatedNewPackages = { ...newPackages };
                                      delete updatedNewPackages[pkg.id];
                                      setNewPackages(updatedNewPackages);

                                      const updatedNotes = [...deliveryNotes];
                                      const noteIndex = updatedNotes.findIndex((n) => {
                                        const existingNote = initialValues?.DeliveryNotes?.find(
                                          (apiNote) => apiNote.id === pkg.delivery_note_id
                                        );
                                        return (
                                          existingNote && n.delivery_note_number === existingNote.delivery_note_number
                                        );
                                      });

                                      if (noteIndex !== -1) {
                                        updatedNotes[noteIndex] = {
                                          ...updatedNotes[noteIndex],
                                          packages: Math.max(0, (updatedNotes[noteIndex].packages || 0) - 1),
                                        };
                                        setDeliveryNotes(updatedNotes);
                                      }
                                    }}
                                    aria-label={t("Delete package")}
                                  >
                                    <DeleteOutlineIcon fontSize="small" style={{ color: "#f44336" }} />
                                  </IconButton>
                                </Paper>
                              </Grid>
                            ))}
                        </Grid>

                        {!readOnly && (
                          <Box mt={2} ml={2}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAddPackagesModal(note.id, noteIndex);
                              }}
                              style={{
                                color: "#1F1F1F",
                                background: "#6F9CEB",
                                marginRight: "15px",
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </Button>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Paper>
            ) : (
              <Paper className={classes.unverifiedSection} elevation={0}>
                <Typography className={classes.noPackagesMessage}>
                  {packagesToDelete.length > 0
                    ? t("All unverified packages have been removed")
                    : t("No unverified packages found")}
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Box>
      <AddPackagesDialog
        addPackagesOpen={addPackagesOpen}
        setAddPackagesOpen={setAddPackagesOpen}
        packageCount={packageCount}
        setPackageCount={setPackageCount}
        handleAddPackages={handleAddPackages}
      />
    </MuiPickersUtilsProvider>
  );
};
export default OrderForm;
